import React from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import Roblox, { CurrentUser, DeviceMeta } from 'Roblox';
import '../../../css/accountSecurityPrompt/accountSecurityPrompt.scss';
import '../../../css/accountSecurityPrompt/banner.scss';
import '../../../css/common/modalModern.scss';
import '../../../css/common/spinner.scss';
import { RequestServiceDefault } from '../../common/request';
import * as PromptAssignments from '../../common/request/types/promptAssignments';
import { PromptAssignment, PromptType } from '../../common/request/types/promptAssignments';
import { TwoStepVerificationError } from '../../common/request/types/twoStepVerification';
import App from './App';
import {
  ACCOUNT_SETTINGS_URL_FRAGMENT,
  LOG_PREFIX,
  REDIRECT_URL_SIGNIFIER,
  ROOT_ELEMENT_ID,
  TRANSLATION_CONFIG
} from './app.config';
import { EventServiceDefault } from './services/eventService';

// Global instance since we do not need prompt parameters for instantiation.
const requestServiceDefault = new RequestServiceDefault();

/**
 * Renders the account security prompt for a given set of parameters.
 */
const renderApp = (
  promptAssignment: PromptAssignment,
  username: string,
  isUserUnder13: boolean
) => {
  const rootElement = document.getElementById(ROOT_ELEMENT_ID);
  if (rootElement !== null) {
    // Remove any existing instances of the app.
    unmountComponentAtNode(rootElement);

    // Instantiate services externally to the app, which will offer future
    // flexibility (e.g. for mocking).
    const eventService = new EventServiceDefault(promptAssignment.promptType);

    // Render the app on the selected element.
    render(
      <App
        promptAssignment={promptAssignment}
        username={username}
        isUserUnder13={isUserUnder13}
        eventService={eventService}
        requestService={requestServiceDefault}
      />,
      rootElement
    );
  }
};

/**
 * Retrieves assignments and renders an account security prompt (based on the
 * currently authenticated user).
 */
const retrieveAndRenderPrompt = async (): Promise<void> => {
  const result = await requestServiceDefault.promptAssignments.getAllForCurrentUser();
  if (result.isError) {
    // eslint-disable-next-line no-console
    console.warn(
      LOG_PREFIX,
      'Retrieving prompt assignments failed with error',
      result.error && PromptAssignments.PromptAssignmentsError[result.error]
    );
    return;
  }

  const assignments = result.value;
  if (assignments.length === 0) {
    // eslint-disable-next-line no-console
    console.log(LOG_PREFIX, 'No assignments were retrieved');
    return;
  }

  // If the user was redirected to this page by a prompt then we don't want
  // another prompt.
  if (
    window.location.href.includes(REDIRECT_URL_SIGNIFIER) ||
    window.location.href.includes(ACCOUNT_SETTINGS_URL_FRAGMENT)
  ) {
    return;
  }

  const promptAssignment = assignments[0];
  // Verify conditions for certain prompt types and return early if needed.
  if (
    promptAssignment.promptType === PromptType.AUTHENTICATOR_UPSELL ||
    promptAssignment.promptType === PromptType.ACCOUNT_RESTORES_POLICY_UPDATE ||
    promptAssignment.promptType === PromptType.ACCOUNT_RESTORES_POLICY_UPDATE_V2
  ) {
    // Check that user doesn't have 2SV authenticator enabled and that they
    // have email verified before showing a prompt.
    const twoStepConfiguration = await requestServiceDefault.twoStepVerification.getUserConfiguration(
      CurrentUser.userId
    );
    if (twoStepConfiguration.isError) {
      // eslint-disable-next-line no-console
      console.warn(
        LOG_PREFIX,
        'Retrieving 2SV configuration failed with error',
        twoStepConfiguration.error && TwoStepVerificationError[twoStepConfiguration.error]
      );
      return;
    }

    const emailConfiguration = await requestServiceDefault.email.getEmailConfiguration();
    if (emailConfiguration.isError) {
      // eslint-disable-next-line no-console
      console.warn(
        LOG_PREFIX,
        'Retrieving email configuration failed with error',
        emailConfiguration.error && TwoStepVerificationError[emailConfiguration.error]
      );
      return;
    }

    if (
      twoStepConfiguration.value.methods.filter(
        method => method.mediaType === 'Authenticator' && method.enabled
      ).length > 0 &&
      emailConfiguration.value.verified
    ) {
      return;
    }
  }

  // Retrieve any translation resources needed to render prompts (currently only
  // supported for generic prompt types).
  if (promptAssignment.isGeneric) {
    const translationQueryResult = await requestServiceDefault.translations.queryAndOverwriteResourcesForNamespace(
      TRANSLATION_CONFIG.feature,
      [promptAssignment.metadata.headerResource, promptAssignment.metadata.bodyResource]
    );
    if (translationQueryResult.isError) {
      // eslint-disable-next-line no-console
      console.warn(
        LOG_PREFIX,
        `Got error code ${translationQueryResult.error?.toString() || 'null'} loading translations`
      );
      return;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  renderApp(promptAssignment, CurrentUser.name, CurrentUser.isUnder13);
};

// Sanity-check that the current user is authenticated and not accessing the
// website using an app-based web view (even to render prompts manually via the
// JavaScript console).
if (CurrentUser && CurrentUser.isAuthenticated && DeviceMeta().appType === 'unknown') {
  // Add some variables to the global `Roblox` object for manual testing via the
  // JavaScript console.
  Object.assign(Roblox, {
    AccountSecurityPrompt: {
      render: renderApp,
      PromptType
    }
  });
  // eslint-disable-next-line no-void
  void retrieveAndRenderPrompt();
}
